import { useEffect } from 'react';
import { BrowserRouter, HashRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';

// Electron preload 会注入 window.electronAPI；运行时动态选路由
const isElectron = typeof window !== 'undefined' && !!window.electronAPI;
const Router = isElectron ? HashRouter : BrowserRouter;
import { LoginPage } from './pages/LoginPage';
import { RegisterPage } from './pages/RegisterPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { CalendarPage } from './pages/CalendarPage';
import { SettingsPage } from './pages/SettingsPage';
import { ReportsPage } from './pages/ReportsPage';
import { PrivacyPage } from './pages/PrivacyPage';
import { TermsPage } from './pages/TermsPage';
import { LandingPage } from './pages/LandingPage';
import { useAuthStore } from './store/auth';

/** 桌面端：根据路由切换透明 / 不透明背景。
 *  - /calendar → 添加 widget-transparent → 显示桌面壁纸
 *  - 其他路由  → 移除 widget-transparent → 恢复原应用背景
 *  必须在 <Router> 内部才能调用 useLocation。
 */
function ElectronRouteSync() {
  const location = useLocation();

  useEffect(() => {
    if (!isElectron) return;
    // HashRouter path 在 location.pathname；hash '/calendar' 会映射为 pathname '/calendar'
    const isCalendar = location.pathname === '/calendar' || location.pathname === '/';
    document.body.classList.toggle('widget-transparent', isCalendar);
  }, [location.pathname]);

  return null;
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const token = useAuthStore((s) => s.accessToken);
  const mode = useAuthStore((s) => s.mode);
  // 本地模式免登录可用；云端模式需 token
  if (!token && mode !== 'local') return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function RedirectIfAuth({ children }: { children: React.ReactNode }) {
  const token = useAuthStore((s) => s.accessToken);
  if (token) return <Navigate to="/calendar" replace />;
  return <>{children}</>;
}

export function App() {
  // 桌面小组件模式：始终保留 is-electron 类（用于 CSS 选择器基础）
  useEffect(() => {
    if (isElectron) {
      document.body.classList.add('is-electron');
    }
    return () => {
      document.body.classList.remove('is-electron');
      document.body.classList.remove('widget-transparent');
    };
  }, []);

  return (
    <Router>
      {/* 桌面端路由变化时同步 body 透明状态 */}
      {isElectron && <ElectronRouteSync />}
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route
          path="/login"
          element={
            <RedirectIfAuth>
              <LoginPage />
            </RedirectIfAuth>
          }
        />
        <Route
          path="/register"
          element={
            <RedirectIfAuth>
              <RegisterPage />
            </RedirectIfAuth>
          }
        />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route
          path="/calendar"
          element={
            <RequireAuth>
              <CalendarPage />
            </RequireAuth>
          }
        />
        <Route
          path="/settings"
          element={
            <RequireAuth>
              <SettingsPage />
            </RequireAuth>
          }
        />
        <Route
          path="/reports"
          element={
            <RequireAuth>
              <ReportsPage />
            </RequireAuth>
          }
        />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}
